// resolve-refs.ts — find a status instance and resolve referenced-creature labels, all from the
// caster blobs already in window.FateTide.casterCache. NO backend query: the status is full-nested
// on its owners' caster blobs (statuses.caster / statuses.target), and caster/target are themselves
// cached creatures. Fail-soft — a missing entry/label yields '' (the renderer shows a neutral fallback).

import type { CasterBlob, StatusEntry } from './types';
import { pick, str } from './read';

type UnknownRecord = Record<string, unknown>;
type CacheMapLike = { values(): Iterable<unknown> };

function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

function cacheMapOf(value: unknown): CacheMapLike | null {
    const record = recordOf(value);
    return record && typeof record.values === 'function'
        ? record as unknown as CacheMapLike
        : null;
}

function cacheRoot(): { casterCache?: unknown; objectCache?: unknown; encounterCache?: unknown } {
    return (window as unknown as { FateTide?: { casterCache?: unknown; objectCache?: unknown; encounterCache?: unknown } }).FateTide ?? {};
}

function getCaster(id: string): CasterBlob | undefined {
    const cache = window.FateTide?.casterCache;
    const value = id && cache ? cache.get(id) : undefined;
    return recordOf(value) as CasterBlob | null ?? undefined;
}

/**
 * ROUND-7 #8 (2026-06-12): cached status rows carry `status_arr` as a bare-UUID catalog REF
 * (the v21 inline def is collapsed). Resolve it against the cached roots' catalogs — full def
 * preferred (the card/forms read layers like status_effect_pausable off it), identity entry as
 * a name/type fallback — on a COPY of the entry (the cached blob is never mutated).
 */
export function findCatalogStatusDef(id: string): Record<string, unknown> | null {
    const ft = cacheRoot();
    let identity: Record<string, unknown> | null = null;
    let itemsOnly: Record<string, unknown> | null = null;
    for (const candidate of [ft.casterCache, ft.objectCache, ft.encounterCache]) {
        const m = cacheMapOf(candidate);
        if (!m) continue;
        for (const root of m.values()) {
            const cat = recordOf(recordOf(root)?.catalog);
            if (!cat) continue;
            const e = recordOf(cat[id] ?? cat[id.toUpperCase()] ?? cat[id.toLowerCase()]);
            if (!e) continue;
            // BEST: a full def (carries layers / computed) — done immediately.
            if (e.items && (e.item_layer_types || e.computed)) return e;
            // NEXT-BEST: an items-only template. A PURE-DESCRIPTION status (Fully Rested / Awake) is just
            // `{items: {…, item_description}}` with NO layers/computed — keep it, because it still carries the
            // item_description + name that the thin identity below would DROP (owner 2026-06-26: the status
            // description vanished on resolve, so the info panel showed nothing under the <hr>).
            if (e.items && itemsOnly === null) itemsOnly = e;
            if (identity === null && (e.item_name !== undefined || e.items)) {
                // Carry item_description through (the server now keeps it on a status_effect identity, so the
                // info panel can show "what it is" for layerless statuses — owner 2026-06-26).
                const items = recordOf(e.items);
                const idDesc = e.item_description ?? items?.item_description;
                identity = {
                    items: {
                        item_id: id,
                        item_name: e.item_name ?? items?.item_name ?? '',
                        item_type: e.item_type ?? items?.item_type ?? '',
                        ...(typeof idDesc === 'string' && idDesc !== '' ? { item_description: idDesc } : {}),
                    },
                };
            }
        }
    }
    return itemsOnly ?? identity;
}

/**
 * The campaign id the pause / resume gate must test — the twin of the PHP list_group's
 * `$lg_campaign_id` (explicit `$campaign_arr` first, then `ft_campaign_context($caster_arr)`).
 *
 * Precedence, and WHY: the page context (`window.ftGetCampaignId()`) wins because it is LITERALLY
 * the value pauseStatusEffect/resumeStatusEffect will POST — `getSidelogContext().campaign_id` is
 * `ftGetCampaignId()` (template.js ftGetEncounterContext). Gating on anything else can paint a
 * button whose mutation then fails, or hide one whose mutation would have worked. The status row's
 * own `status_campaign_id` is the fallback for a cold page context; it rides on caster blobs
 * (_ft_elide_status_context_dups only elides it on encounter-rooted blobs, where it equals the
 * blob's own campaign anyway).
 *
 * Do NOT reach for `caster.encounter_creatures.campaign_id` — that column does not exist. The
 * player-character key is `attached_campaign_id`, not `campaign_id`; reading the wrong names is
 * what made this resolve to '' on every blob and suppressed the Pause button entirely.
 */
export function statusCampaignId(entry?: StatusEntry, ctxCampaignId?: string): string {
    const fromCtx = str(ctxCampaignId);
    if (fromCtx) return fromCtx;
    let fromPage = '';
    try { fromPage = str(window.ftGetCampaignId?.()); } catch { /* template.js not loaded yet */ }
    if (fromPage) return fromPage;
    return entry ? str(pick(entry, 'status_campaign_id')) : '';
}

/**
 * The encounter id half of the same gate. pause/resume_status_effect_inc.php require BOTH a campaign
 * AND an encounter ("Encounter context is required to pause a status effect."), so gating on the
 * campaign alone can still paint a button whose mutation dies. There is no encounter id on a status
 * row (a row carries status_campaign_id but no encounter column), so this is ctx-then-page only.
 *
 * Whatever this and statusCampaignId return is what the caller PASSES to pauseStatusEffect /
 * resumeStatusEffect as overrides — the gate and the mutation therefore use the same context by
 * construction, instead of the gate reading one source and the mutation posting another.
 */
export function statusEncounterId(ctxEncounterId?: string): string {
    const fromCtx = str(ctxEncounterId);
    if (fromCtx) return fromCtx;
    try { return str(window.ftGetActiveEncounterId?.()); } catch { return ''; }
}

export function resolvedStatusEntry(entry: StatusEntry | undefined): StatusEntry | undefined {
    if (!entry || typeof entry !== 'object') return entry;
    // ROUND-12 #5 (v33): self-cast rows elide status_caster_id at intern (== target) —
    // re-stamp on the returned copy (a stored EMPTY caster id passes through untouched).
    const selfCast = !('status_caster_id' in entry)
        && typeof entry.status_target_id === 'string' && entry.status_target_id !== '';
    // ROUND-9 #11: the collapsed ref rides under `status_item_id` (honest DB column name);
    // a bare string under status_arr is the legacy v27/v28 warm-blob form. Either way the
    // resolved def is presented under status_arr on the returned copy.
    const sa = entry.status_arr;
    const sid = entry.status_item_id;
    const ref = (sa != null && typeof sa === 'object')
        ? '' // fresh full-def row — no resolve needed
        : ((typeof sid === 'string' && sid !== '')
            ? sid
            : ((typeof sa === 'string' && sa !== '') ? sa : ''));
    if (ref === '' && !selfCast) return entry;
    const out: StatusEntry = { ...entry };
    if (selfCast) out.status_caster_id = entry.status_target_id;
    if (ref !== '') out.status_arr = findCatalogStatusDef(ref) ?? { items: { item_id: ref } };
    return out;
}

/** Find a status instance (by status_id) in a caster blob's .caster then .target lists. */
export function findStatusEntry(caster: CasterBlob | undefined, statusId: string): StatusEntry | undefined {
    if (!caster || !statusId) return undefined;
    // Case-insensitive id match: UUIDs are stored uppercase, but the open-side-info registry also
    // uppercases the id it hands back on a WS repaint — compare normalized so either casing resolves.
    const want = statusId.toUpperCase();
    for (const list of [caster.statuses?.caster, caster.statuses?.target]) {
        if (Array.isArray(list)) {
            const hit = list.find((e) => str(e?.status_id).toUpperCase() === want);
            if (hit) return resolvedStatusEntry(hit);
        }
    }
    return undefined;
}

/** Pull a {name,type} off a creature/inventory blob row (caster-cache OR encounter-member shape). */
interface LabelRow {
    player_characters?: { player_name?: unknown };
    encounter_creatures?: { encounter_creature_name?: unknown; encounter_creature_id?: unknown };
    items?: { item_name?: unknown; item_type?: unknown; item_id?: unknown };
    item_name?: unknown;
    item_type?: unknown;
    inventory_id?: unknown;
}

function labelRow(value: unknown): LabelRow | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as LabelRow
        : undefined;
}

function labelFromRow(c: LabelRow | undefined): { name: string; type: string } {
    if (!c) return { name: '', type: '' };
    const name =
        str(c.player_characters?.player_name) ||
        str(c.encounter_creatures?.encounter_creature_name) ||
        str(c.items?.item_name) ||
        str(c.item_name) ||
        '';
    const type = str(c.items?.item_type) || str(c.item_type) || '';
    return { name, type };
}

/**
 * Best-effort display label (name + type) for a creature/inventory id. The warm caster cache first
 * (a viewed caster), then a scan of the ENCOUNTER cache's member rows — a status's caster/target is
 * often a creature NOT currently being viewed (e.g. the Test Dragon under another player's POV), so it
 * never lands in casterCache, but it's always on the encounter blob.
 */
export function creatureLabel(id: string): { name: string; type: string } {
    if (!id) return { name: '', type: '' };
    const fromCaster = labelFromRow(getCaster(id));
    if (fromCaster.name) return fromCaster;

    const ec = cacheMapOf(cacheRoot().encounterCache);
    if (ec) {
        for (const enc of ec.values()) {
            const encounter = recordOf(enc);
            if (!encounter) continue;
            for (const listKey of ['encounter_creatures_arr', 'encounter_inventories_arr']) {
                const list = encounter[listKey];
                const rows = Array.isArray(list) ? list : (list && typeof list === 'object' ? Object.values(list) : []);
                for (const value of rows) {
                    const row = recordOf(value);
                    if (!row) continue;
                    const encounterCreature = recordOf(row.encounter_creatures);
                    const items = recordOf(row.items);
                    const rowId = str(encounterCreature?.encounter_creature_id)
                        || str(row.inventory_id)
                        || str(items?.item_id);
                    if (rowId === id) {
                        const lbl = labelFromRow(row as LabelRow);
                        if (lbl.name) return lbl;
                    }
                }
            }
        }
    }
    return fromCaster; // {'',''} — caller falls back to its own name then 'Unknown'
}
